import {
  Alert,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Row,
  Image,
  message,
  TabsProps,
  Tabs,
} from 'antd'
import React, {useEffect, useState} from 'react'
import {Plant, DatabasePlantRecordMetadata, PlantWithProjectName} from '../../entities/plant'
import {dateFormat} from '../../entities/todoList'
import {PlantDatabaseSearchField} from './PlantDatabaseSearchField'
import {ElementFormDropdown} from './components/ElementFormDropdown'
import {ProjectFormDropdown} from './components/ProjectFormDropdown'
import {renderFieldGroup} from './components/renderFieldGroup'
import {useIntl} from 'react-intl'
import {useCurrentUser} from '../../hooks/useCurrentUser'
import {DatabasePlantRecord, instance as plantsService} from '../../services/PlantsService'
import {prepareFormFields} from './prepareFormFields'
import {comparePlantFields, groupBy} from '../../services/util'
import {PlantFormProps} from './PlantFormProps'
import {useRevalidator, useRouteLoaderData, useSubmit} from 'react-router-dom'
import {Project} from '../../entities/project'
import {GardenPlanVariant} from '../../entities/gardenPlanVariant'
import {useGardenPlanVariantParam} from '../../hooks/useGardenPlanVariantParam'
import {useAsyncValue} from '../../hooks/useAsyncValue'
import {instance as gardenPlanVariantsService} from '../../services/GardenPlanVariantsService'
import {ImagePreview} from './components/ImagePreview'
import {InfoCircleOutlined} from '@ant-design/icons'
import {BasePlantSelector} from './BasePlantSelector'
import dayjs from 'dayjs'
import {FontAwesomeIcon as Icon} from '@fortawesome/react-fontawesome'
import * as IconsRegular from '@fortawesome/free-regular-svg-icons'
import * as Icons from '@fortawesome/free-solid-svg-icons'
import {Language} from '../../services/language'
import {Panel} from './components/Panel'
import './plantForm.less'

const validateMessages = {
  required: '${label} is required!',
}

const normalizeFile = (e: any) => {
  if (Array.isArray(e)) {
    return e
  }
  return e?.fileList
}

export type PlantFormEditContentProps = {
  currentPlant?: PlantWithProjectName | null
  setMode: (mode: string) => void
  plantTitlePhoto: string | null
  projectsList: PlantFormProps['projectsList']
  onClose: () => void
  preventFormClose: (reasonMessage: string) => void
  mode: 'view' | 'edit' | 'create'
}

export const PlantFormEditContent = ({
  currentPlant,
  setMode,
  plantTitlePhoto,
  projectsList,
  onClose,
  preventFormClose,
  mode,
}: PlantFormEditContentProps) => {
  const intl = useIntl()
  const user = useCurrentUser()
  const [form] = Form.useForm()
  const [basePlantId, setBasePlantId] = useState(currentPlant?.plantBaseId)
  const [basePlantTitlePhoto, setBasePlantTitlePhoto] = useState(plantTitlePhoto)
  const [isSubmitting, setSubmitting] = useState(false)
  const [isLoading, setLoading] = useState(true)
  const files = Form.useWatch('files', form)
  const newFiles = Form.useWatch('newFiles', form)
  const submit = useSubmit()

  const [messageApi, contextHolder] = message.useMessage()

  const language = intl.locale as Language

  const projectRouteData = (useRouteLoaderData('project') as any) || {}
  const projectFromRoute = (projectRouteData.project as Project) || undefined
  const {gardenPlanVariantId} = useGardenPlanVariantParam()
  const gardenPlanVariantForNewPlant =
    mode === 'create'
      ? ((projectRouteData.projectGardenPlanVariants || []) as GardenPlanVariant[]).find(
          (variant) => variant.id === gardenPlanVariantId
        )
      : undefined
  const {value: currentPlantGardenPlanVariantName} = useAsyncValue(
    undefined as string | undefined,
    async () => {
      if (!currentPlant?.gardenPlanVariantId || !currentPlant.projectId) {
        return undefined
      }

      const result = await gardenPlanVariantsService.getProjectGardenPlanVariants(
        currentPlant.projectId
      )
      return result.success
        ? result.projectGardenPlanVariants.find(
            (variant) => variant.id === currentPlant.gardenPlanVariantId
          )?.name
        : undefined
    },
    [currentPlant?.gardenPlanVariantId, currentPlant?.projectId]
  )
  const gardenPlanVariantName =
    gardenPlanVariantForNewPlant?.name ?? currentPlantGardenPlanVariantName
  const isProjectLocked =
    !!currentPlant?.gardenPlanVariantId || (mode === 'create' && !!projectFromRoute)
  const selectedProjectId = Form.useWatch('projectId', form)
  useEffect(() => {
    form.setFieldValue('projectId', currentPlant?.projectId || projectFromRoute?.id)
  }, [projectFromRoute?.id])

  useEffect(() => {
    form.setFieldValue(
      'elementId',
      selectedProjectId === currentPlant?.projectId ? currentPlant?.elementId : undefined
    )
  }, [selectedProjectId, currentPlant?.projectId])

  useEffect(() => {
    ;(async () => {
      setLoading(true)
      try {
        form.setFieldsValue({
          ...(currentPlant?.fields || {}),
          name: currentPlant?.name || '',
          files: currentPlant?.photos || [],
          comment: currentPlant?.comment || '',
          dateOfPlanting: currentPlant?.dateOfPlantingUtc
            ? dayjs(currentPlant.dateOfPlantingUtc, dateFormat)
            : null,
        })
      } finally {
        setLoading(false)
      }
    })()
  }, [currentPlant])

  const fieldGroups = groupBy(
    Object.entries(DatabasePlantRecordMetadata),
    ([key, field]) => field.group
  )

  const preventFormCloseDueToModification = () => {
    preventFormClose(intl.formatMessage({id: 'formModifiedButNotSaved'}))
  }

  const items: TabsProps['items'] = [
    {
      key: '1',
      label: (
        <span className={'edit-plant-form__tabs__label'}>
          <Icon icon={Icons.faLeaf} />
          <span>{intl.formatMessage({id: 'taxonomy'})}</span>
        </span>
      ),
      children: renderFieldGroup(fieldGroups['main'], preventFormCloseDueToModification),
    },
    {
      key: '2',
      forceRender: true,
      label: (
        <span className={'edit-plant-form__tabs__label'}>
          <Icon icon={Icons.faMapMarkerAlt} />
          <span>{intl.formatMessage({id: 'locationPlant'})}</span>
        </span>
      ),
      children: (
        <div>
          {!currentPlant && !gardenPlanVariantForNewPlant && (
            <Form.Item name="dateOfPlanting" label={intl.formatMessage({id: 'dateOfPlanting'})}>
              <DatePicker />
            </Form.Item>
          )}
          <ProjectFormDropdown
            projectsList={projectsList}
            preventFormCloseDueToModification={preventFormCloseDueToModification}
            disabled={isProjectLocked}
            extra={
              gardenPlanVariantName && (
                <Alert
                  type="warning"
                  showIcon
                  title={intl.formatMessage(
                    {id: 'plantIsCreatedInGardenPlan'},
                    {planName: gardenPlanVariantName}
                  )}
                />
              )
            }
          />
          <ElementFormDropdown
            selectedProjectId={selectedProjectId}
            projectsList={projectsList}
            preventFormCloseDueToModification={preventFormCloseDueToModification}
          />
          <Form.Item name="comment" label={intl.formatMessage({id: 'comment'})}>
            <Input.TextArea />
          </Form.Item>
        </div>
      ),
    },
    {
      key: '3',
      label: (
        <span className={'edit-plant-form__tabs__label'}>
          <Icon icon={Icons.faInfoCircle} />
          <span>{intl.formatMessage({id: 'characteristics'})}</span>
        </span>
      ),
      children: (
        <div>
          {renderFieldGroup(fieldGroups['common'], preventFormCloseDueToModification)}
          <Panel title="overview" backgroundIcon={Icons.faClipboardList}>
            {renderFieldGroup(fieldGroups['overview'], preventFormCloseDueToModification)}
          </Panel>
          <Panel title="growing-conditions" backgroundIcon={Icons.faMapMarkerAlt}>
            {renderFieldGroup(fieldGroups['growing-conditions'], preventFormCloseDueToModification)}
          </Panel>
          <Panel title="size-appearance" backgroundIcon={Icons.faArrowsAltH}>
            {renderFieldGroup(fieldGroups['size-appearance'], preventFormCloseDueToModification)}
          </Panel>
          <Panel title="properties" backgroundIcon={Icons.faDna}>
            {renderFieldGroup(fieldGroups['properties'], preventFormCloseDueToModification)}
          </Panel>
        </div>
      ),
    },
    {
      key: '4',
      label: (
        <span className={'edit-plant-form__tabs__label'}>
          <Icon icon={Icons.faDroplet} />
          <span>{intl.formatMessage({id: 'care'})}</span>
        </span>
      ),
      children: (
        <div>
          {renderFieldGroup(fieldGroups['care'], preventFormCloseDueToModification)}
          <Panel title="plant-health" backgroundIcon={Icons.faPlantWilt}>
            {renderFieldGroup(fieldGroups['plant-health'], preventFormCloseDueToModification)}
          </Panel>
          <Panel title="fruiting" backgroundIcon={Icons.faAppleWhole}>
            {renderFieldGroup(fieldGroups['fruiting'], preventFormCloseDueToModification)}
          </Panel>
        </div>
      ),
    },
    {
      key: '5',
      label: (
        <span className={'edit-plant-form__tabs__label'}>
          <Icon icon={Icons.faSeedling} />
          <span>{intl.formatMessage({id: 'propagation-planting'})}</span>
        </span>
      ),
      children: renderFieldGroup(
        fieldGroups['propagation-planting'],
        preventFormCloseDueToModification
      ),
    },
  ]

  return (
    <Form
      form={form}
      labelCol={{span: 8}}
      wrapperCol={{span: 16}}
      labelWrap
      autoComplete="off"
      layout="horizontal"
      validateMessages={validateMessages}
      onChange={preventFormCloseDueToModification}
      onFinish={async (formState) => {
        setSubmitting(true)
        let result: {success: boolean; error?: string; id?: number}
        const plant: Omit<Plant, 'id'> = {
          name: formState.name,
          dateOfPlantingUtc: formState.dateOfPlanting, //?.format(dateFormat),
          userId: user.id,
          comment: formState.comment,
          projectId: formState.projectId,
          gardenPlanVariantId: currentPlant
            ? currentPlant.gardenPlanVariantId
            : gardenPlanVariantForNewPlant?.id,
          elementId: formState.elementId || null,
          confidenceInPlantVariety: formState.confidenceInPlantVariety,
          overwrittenBaseFields: currentPlant ? currentPlant.overwrittenBaseFields : {},
          plantBaseId: basePlantId,
          fields: undefined,
        }

        if (currentPlant) {
          //const {projectName, ...existingPlant} = currentPlant
          result = await plantsService.editPlant(
            {
              id: currentPlant.id,
              collections: currentPlant.collections,
              ...plant,
              overwrittenBaseFields: formState,
            },
            language
            //{...existingPlant, ...plant}
            //formState.files.map((file, index) => file.photoName),
            //formState.newFiles
          )
        } else {
          result = await plantsService.createPlant(
            {
              ...plant,
              overwrittenBaseFields: formState,
            },
            formState.newFiles,
            language,
            basePlantId
          )
        }
        setSubmitting(false)
        if (result.success) {
          submit(result.id ? {id: String(result.id)} : null, {method: 'post'})
          if (currentPlant) {
            setMode('view')
          }
        } else {
          messageApi.open({
            type: 'error',
            content: result.error,
          })
        }
      }}
    >
      <div className={'edit-plant-form__top'}>
        <h2>
          <span>
            {mode === 'create'
              ? intl.formatMessage({id: 'addingNewPlant'})
              : intl.formatMessage({id: 'editingPlant'})}
          </span>
        </h2>

        <Form.Item>
          <span className={'edit-plant-form__top__save-cancel'}>
            <Button onClick={() => (mode === 'create' ? onClose() : setMode('view'))}>
              {intl.formatMessage({id: 'cancel'})}
            </Button>

            <Button htmlType="submit" type="primary">
              {intl.formatMessage({id: 'save'})}
            </Button>
          </span>
        </Form.Item>
      </div>
      <Row className={'edit-plant-form__header'}>
        {basePlantTitlePhoto && (
          <Col className={'edit-plant-form__header__photo'}>
            <Image src={basePlantTitlePhoto}></Image>
          </Col>
        )}
        <Col className={'edit-plant-form__header__fields'}>
          <Form.Item
            name="name"
            label={intl.formatMessage({id: 'plantName'})}
            rules={[{required: true}]}
            tooltip={{
              title: intl.formatMessage({id: 'plantName-tooltip'}),
              icon: <InfoCircleOutlined />,
            }}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="base"
            label={intl.formatMessage({id: 'basePlant'})}
            tooltip={{
              title: intl.formatMessage({id: 'basePlant-tooltip'}),
              icon: <InfoCircleOutlined />,
            }}
          >
            <BasePlantSelector
              basePlantId={basePlantId}
              onBasePlantSelected={async (dbPlant, fieldsToClear, titlePhoto) => {
                setBasePlantId(dbPlant === null ? undefined : dbPlant.id)

                const preparedFieldsToClear = await prepareFormFields(undefined, fieldsToClear)

                for (const fieldName of Object.keys(preparedFieldsToClear)) {
                  if (comparePlantFields(form.getFieldValue(fieldName), fieldsToClear[fieldName])) {
                    form.setFieldValue(fieldName, undefined)
                  }
                }

                form.setFieldsValue(
                  await prepareFormFields(currentPlant || undefined, dbPlant ? dbPlant?.fields : {})
                )

                setBasePlantTitlePhoto(titlePhoto)
              }}
            />
          </Form.Item>
        </Col>
      </Row>
      <Tabs className={'edit-plant-form__tabs'} defaultActiveKey="1" items={items} />
    </Form>
  )
}
